import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminService from '../../../services/adminService';
import WizardSubmissionService from '../../../services/wizardSubmissionService';
import AILeadRankingService from '../../../services/aiLeadRankingService';
import { LoadingSpinner, EmptyState, PageHeader, ActionButton, StatusBadge, FilterBar } from '../../../components/ui';

/**
 * Wizard Submissions Manager Component
 * Displays and manages completed wizard submissions
 */
const WizardSubmissionsManager = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [sortBy, setSortBy] = useState('priority'); // 'priority', 'date', 'value'
  const [showDashboard, setShowDashboard] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  useEffect(() => {
    if (submissions.length > 0) {
      calculateRankings();
      loadDashboardStats();
    }
  }, [submissions]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (filter !== 'all') {
        filters.status = filter;
      }

      const data = await AdminService.getWizardSubmissions(filters);
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error loading wizard submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const calculateRankings = async () => {
    try {
      setLoadingRankings(true);
      console.log('🎯 Calculating AI rankings for', submissions.length, 'submissions');

      const calculatedRankings = await AILeadRankingService.batchCalculateRankings(submissions);
      setRankings(calculatedRankings);

      console.log('✅ AI rankings calculated for', calculatedRankings.length, 'submissions');
    } catch (error) {
      console.error('Error calculating rankings:', error);
    } finally {
      setLoadingRankings(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const stats = await AILeadRankingService.getDashboardStatistics(submissions);
      setDashboardStats(stats);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      await AdminService.updateWizardSubmissionStatus(submissionId, newStatus);
      await loadSubmissions(); // Reload data
    } catch (error) {
      console.error('Error updating submission status:', error);
      setError('Failed to update submission status');
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await AdminService.deleteWizardSubmission(submissionId);
        await loadSubmissions(); // Reload data
      } catch (error) {
        console.error('Error deleting submission:', error);
        setError('Failed to delete submission');
      }
    }
  };

  // ============= BULK ACTION FUNCTIONS =============

  // Filter submissions based on search term (must be defined before functions that use it)
  const filteredSubmissions = submissions.filter(submission => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      submission.first_name?.toLowerCase().includes(searchLower) ||
      submission.last_name?.toLowerCase().includes(searchLower) ||
      submission.email?.toLowerCase().includes(searchLower) ||
      submission.phone?.toLowerCase().includes(searchLower) ||
      submission.selected_pathway?.toLowerCase().includes(searchLower)
    );
  });

  // Toggle single selection
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all visible submissions
  const selectAll = () => {
    const allIds = filteredSubmissions.map(s => s.id);
    setSelectedIds(new Set(allIds));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  // Check if all visible are selected
  const allSelected = filteredSubmissions.length > 0 &&
    filteredSubmissions.every(s => selectedIds.has(s.id));

  // Bulk status update
  const bulkUpdateStatus = async (newStatus) => {
    try {
      setBulkActionLoading(true);
      const promises = Array.from(selectedIds).map(id =>
        AdminService.updateWizardSubmissionStatus(id, newStatus)
      );
      await Promise.all(promises);
      setShowBulkConfirm(null);
      setSelectedIds(new Set());
      await loadSubmissions();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      setError('Failed to update some submissions');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete
  const bulkDelete = async () => {
    try {
      setBulkActionLoading(true);
      const promises = Array.from(selectedIds).map(id =>
        AdminService.deleteWizardSubmission(id)
      );
      await Promise.all(promises);
      setShowBulkConfirm(null);
      setSelectedIds(new Set());
      await loadSubmissions();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      setError('Failed to delete some submissions');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const selected = submissions.filter(s => selectedIds.has(s.id));
    const headers = [
      'First Name', 'Last Name', 'Email', 'Phone',
      'Country', 'Pathway', 'Package', 'Status',
      'Created At'
    ];

    const rows = selected.map(s => [
      s.first_name || '',
      s.last_name || '',
      s.email || '',
      s.phone || '',
      s.country_selection?.selectedCountry || '',
      s.selected_pathway || '',
      s.selected_package || '',
      s.status || 'submitted',
      s.created_at || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wizard_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // filteredSubmissions is defined above with bulk action functions

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPackageDetails = (submission) => {
    if (!submission.selected_package) return 'No package selected';

    const packageNames = {
      'essential': 'Essential Package',
      'premium': 'Premium Package',
      'essential-DEU': 'Essential Package - Germany',
      'premium-DEU': 'Premium Package - Germany'
    };

    return packageNames[submission.selected_package] || submission.selected_package;
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `Rs ${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `Rs ${(amount / 1000).toFixed(0)}K`;
    } else {
      return `Rs ${amount.toLocaleString()}`;
    }
  };

  const getPriorityRanking = (submissionId) => {
    return rankings.find(r => r.submissionId === submissionId);
  };

  const getPriorityColor = (priorityLevel) => {
    switch (priorityLevel) {
      case 'HIGH': return 'bg-red-100 text-red-800 border-red-200';
      case 'MEDIUM-HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW-MEDIUM': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'LOW': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priorityLevel) => {
    switch (priorityLevel) {
      case 'HIGH':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
          </svg>
        );
      case 'MEDIUM-HIGH':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getSortedSubmissions = () => {
    const submissionsWithRankings = filteredSubmissions.map(submission => ({
      ...submission,
      ranking: getPriorityRanking(submission.id)
    }));

    switch (sortBy) {
      case 'priority':
        return submissionsWithRankings.sort((a, b) =>
          (b.ranking?.priorityScore?.score || 0) - (a.ranking?.priorityScore?.score || 0)
        );
      case 'value':
        return submissionsWithRankings.sort((a, b) =>
          (b.ranking?.estimatedValue?.estimatedValue || 0) - (a.ranking?.estimatedValue?.estimatedValue || 0)
        );
      case 'date':
      default:
        return submissionsWithRankings.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading submissions..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      <PageHeader
        darkMode={true}
        title="Wizard Submissions"
        subtitle={`${submissions.length} total submission${submissions.length !== 1 ? 's' : ''}${dashboardStats ? ` • ${dashboardStats.highPriority} high priority` : ''}`}
        icon="🎯"
        action={
          <div className="flex items-center gap-2">
            <ActionButton onClick={() => setShowDashboard(!showDashboard)} variant="secondary">
              {showDashboard ? '📊 Hide' : '📊 Show'}
            </ActionButton>
            <ActionButton onClick={loadSubmissions} variant="primary">
              🔄
            </ActionButton>
          </div>
        }
      />

      {/* AI Dashboard */}
      {showDashboard && dashboardStats && (
        <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-xl p-4 border-2 border-blue-800 shadow-lg">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-xl">🤖</span>
            <span>AI Lead Intelligence</span>
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-lg font-bold text-white">{dashboardStats.totalSubmissions}</p>
                </div>
                <div className="text-blue-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 border border-red-900/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">High Priority</p>
                  <p className="text-lg font-bold text-red-400">{dashboardStats.highPriority}</p>
                </div>
                <div className="text-red-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 border border-green-900/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Avg Score</p>
                  <p className="text-lg font-bold text-green-400">{dashboardStats.averagePriorityScore}</p>
                </div>
                <div className="text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 border border-purple-900/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Est. Value</p>
                  <p className="text-sm font-bold text-purple-400">{formatCurrency(dashboardStats.estimatedTotalValue)}</p>
                </div>
                <div className="text-purple-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Top Countries */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <h4 className="font-semibold text-white mb-2 text-sm">Top Countries</h4>
              <div className="space-y-1">
                {dashboardStats.topCountries.map((country, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-xs text-gray-400">{country.country}</span>
                    <span className="text-xs font-medium text-white">{country.count} leads</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <h4 className="font-semibold text-white mb-2 text-sm">Urgency</h4>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">🔥 Critical</span>
                  <span className="text-xs font-medium text-red-600">{dashboardStats.urgencyBreakdown.critical}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">⭐ High</span>
                  <span className="text-xs font-medium text-orange-400">{dashboardStats.urgencyBreakdown.high}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">📊 Medium</span>
                  <span className="text-xs font-medium text-yellow-400">{dashboardStats.urgencyBreakdown.medium}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">📋 Low</span>
                  <span className="text-xs font-medium text-gray-400">{dashboardStats.urgencyBreakdown.low}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <FilterBar
        darkMode={true}
        searchPlaceholder="Search by name, email, phone, or pathway..."
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'status',
            label: 'Filter by status',
            value: filter,
            options: [
              { value: 'all', label: 'All Status' },
              { value: 'pending', label: 'Pending' },
              { value: 'contacted', label: 'Contacted' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' }
            ]
          },
          {
            key: 'sort',
            label: 'Sort by',
            value: sortBy,
            options: [
              { value: 'priority', label: 'Priority' },
              { value: 'value', label: 'Value' },
              { value: 'date', label: 'Date' }
            ]
          }
        ]}
        onFilterChange={(key, value) => {
          if (key === 'status') setFilter(value);
          if (key === 'sort') setSortBy(value);
        }}
      />

      {/* Bulk Action Toolbar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-900/50 border border-blue-600 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
          <div className="text-sm text-blue-300 font-medium">
            {selectedIds.size} selected
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={deselectAll}
              className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
            >
              Clear Selection
            </button>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  setShowBulkConfirm({ type: 'status', status: e.target.value });
                }
              }}
              defaultValue=""
              className="px-3 py-1.5 text-xs bg-gray-700 border border-gray-600 text-white rounded"
            >
              <option value="">Change Status...</option>
              <option value="submitted">Submitted</option>
              <option value="reviewed">Reviewed</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="closed">Closed</option>
            </select>

            <button
              onClick={exportToCSV}
              className="px-3 py-1.5 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>

            <button
              onClick={() => setShowBulkConfirm({ type: 'delete' })}
              className="px-3 py-1.5 text-xs bg-red-700 hover:bg-red-600 text-white rounded transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {showBulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-3">
              {showBulkConfirm.type === 'delete' ? '🗑️ Confirm Delete' : '📋 Confirm Status Change'}
            </h3>
            <p className="text-gray-300 mb-4">
              {showBulkConfirm.type === 'delete'
                ? `Are you sure you want to delete ${selectedIds.size} submission(s)? This cannot be undone.`
                : `Change status of ${selectedIds.size} submission(s) to "${showBulkConfirm.status}"?`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBulkConfirm(null)}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => showBulkConfirm.type === 'delete' ? bulkDelete() : bulkUpdateStatus(showBulkConfirm.status)}
                disabled={bulkActionLoading}
                className={`px-4 py-2 text-white rounded transition-colors flex items-center gap-2 ${showBulkConfirm.type === 'delete'
                  ? 'bg-red-600 hover:bg-red-500'
                  : 'bg-blue-600 hover:bg-blue-500'
                  }`}
              >
                {bulkActionLoading && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {showBulkConfirm.type === 'delete' ? 'Delete' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions List */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        {filteredSubmissions.length === 0 ? (
          <EmptyState
            icon="📝"
            title="No submissions found"
            description={searchTerm || filter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Completed wizard submissions will appear here'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  {/* Checkbox Column Header */}
                  <th className="px-2 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => allSelected ? deselectAll() : selectAll()}
                      className="w-4 h-4 rounded border-gray-500 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Conv%
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filteredSubmissions.map((submission) => {
                  const ranking = getPriorityRanking(submission.id);
                  const isSelected = selectedIds.has(submission.id);
                  return (
                    <tr
                      key={submission.id}
                      className={`hover:bg-gray-700 ${isSelected ? 'bg-blue-900/20' : ''}`}
                    >
                      {/* Checkbox Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(submission.id)}
                          className="w-4 h-4 rounded border-gray-500 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                        />
                      </td>
                      {/* Priority Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        {ranking ? (
                          <div className="flex items-center">
                            <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full border ${getPriorityColor(ranking.priorityLevel)}`}>
                              {ranking.priorityLevel}
                            </span>
                            <div className="ml-1 text-xs text-gray-500">
                              #{ranking.priorityRank}
                            </div>
                          </div>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-gray-700 text-gray-200 border-gray-600">
                            PENDING
                          </span>
                        )}
                      </td>

                      {/* Name Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div>
                          <div className="text-xs font-medium text-white">
                            {submission.first_name} {submission.last_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {submission.submission_id?.substring(0, 8)}...
                          </div>
                        </div>
                      </td>

                      {/* Contact Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="text-xs text-white">
                          <div className="truncate max-w-[80px]">{submission.email}</div>
                          <div className="text-gray-400">{submission.phone}</div>
                        </div>
                      </td>

                      {/* Score Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        {ranking ? (
                          <div className="text-xs">
                            <div className="font-medium text-white">
                              {ranking.priorityScore?.opportunityScore || 0}/100
                            </div>
                            <div className="text-gray-400">
                              {ranking.priorityScore?.basePathwayScore || 0}%
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">
                            --
                          </div>
                        )}
                      </td>

                      {/* Conversion Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        {ranking ? (
                          <div className="text-xs">
                            <div className="font-medium text-white">
                              {ranking.conversionProbability}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1 mt-0.5">
                              <div
                                className={`h-1 rounded-full ${ranking.conversionProbability >= 70 ? 'bg-green-500' :
                                  ranking.conversionProbability >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                style={{ width: `${ranking.conversionProbability}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">
                            --
                          </div>
                        )}
                      </td>

                      {/* Value Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        {ranking ? (
                          <div className="text-xs">
                            <div className="font-medium text-white">
                              {formatCurrency(ranking.estimatedValue?.estimatedValue || 0)}
                            </div>
                            <div className="text-gray-400">
                              {ranking.salesUrgency === 'critical' ? '🔥' :
                                ranking.salesUrgency === 'high' ? '⭐' :
                                  ranking.salesUrgency === 'medium' ? '📊' : '📋'}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">
                            --
                          </div>
                        )}
                      </td>

                      {/* Country Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="text-xs">
                          <div className="font-medium text-white">
                            {submission.country_selection?.selectedCountry || 'N/A'}
                          </div>
                          <div className="text-gray-400">
                            {getPackageDetails(submission)}
                          </div>
                        </div>
                      </td>

                      {/* Date Column */}
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-400">
                        {formatDate(submission.created_at)}
                      </td>

                      {/* Status Column */}
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusColor(submission.status || 'pending')}`}>
                          {(submission.status || 'pending').charAt(0).toUpperCase() + (submission.status || 'pending').slice(1)}
                        </span>
                      </td>

                      {/* Actions Column */}
                      <td className="px-2 py-2 whitespace-nowrap text-right text-xs font-medium">
                        <div className="flex justify-end gap-1">
                          <Link
                            to={`/admin/submissions/${submission.id}`}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            View
                          </Link>
                          <select
                            value={submission.status || 'submitted'}
                            onChange={(e) => updateSubmissionStatus(submission.id, e.target.value)}
                            className="text-xs border border-gray-600 rounded px-1 py-0.5 bg-gray-700 text-white"
                          >
                            <option value="submitted">Submitted</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                            <option value="closed">Closed</option>
                          </select>
                          <button
                            onClick={() => deleteSubmission(submission.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal removed - now using dedicated detail page */}
    </div>
  );
};

export default WizardSubmissionsManager;